import { ROLE } from '@cmoh/shared'
import { lazy, type ReactElement, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { AppSuspenseFallback } from '@/components/AppSuspenseFallback'
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary'
import { InMaintenancePage } from '@/features/auth/pages/InMaintenancePage'
import { PerformanceHomeSwitch } from '@/features/performance-home/components/PerformanceHomeSwitch'
import { AppLayout } from '@/layout/AppLayout'
import {
  AUTHENTICATED_LAYOUT_ROUTE_ID,
  keepOrganizationInUrl,
} from '@/lib/routing/organization-url-sync'
import { collectFeatureRoutes } from '@/lib/routing/routing.collector'
import { toRouteObjects } from '@/lib/routing/routing.compose'
import { PermissionGate } from '@/lib/routing/routing.guard'
import { TabOrganizationGate } from '@/lib/routing/tab-organization.gate'

const LoadingFallback: ReactElement = <AppSuspenseFallback />

const RouteError: ReactElement = <RouteErrorBoundary />

// Base pages
const Forbidden = lazy(() => import('@/features/errors/pages/Forbidden'))
const NotFound = lazy(() => import('@/features/errors/pages/NotFound'))
const Login = lazy(() => import('@/features/auth/pages/LoginPage'))
const OAuthConsent = lazy(() => import('@/features/oauth-consent/pages/OAuthConsentPage'))
const Onboarding = lazy(() => import('@/features/onboarding/pages/OnboardingPage'))

const OrganizationForbidden: ReactElement = (
  <Suspense fallback={LoadingFallback}>
    <Forbidden />
  </Suspense>
)

const featureRoutes = toRouteObjects(collectFeatureRoutes())

export const router = createBrowserRouter([
  {
    id: AUTHENTICATED_LAYOUT_ROUTE_ID,
    path: '/',
    element: (
      <Suspense fallback={LoadingFallback}>
        <TabOrganizationGate rejectedFallback={OrganizationForbidden}>
          <PermissionGate minRole={ROLE.VIEWER} loadingFallback={LoadingFallback}>
            <AppLayout />
          </PermissionGate>
        </TabOrganizationGate>
      </Suspense>
    ),
    errorElement: RouteError,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={LoadingFallback}>
            <PerformanceHomeSwitch />
          </Suspense>
        ),
        errorElement: RouteError,
        handle: { meta: { title: 'basePages.home' } },
      },
      ...featureRoutes,
    ],
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={LoadingFallback}>
        <Login />
      </Suspense>
    ),
    errorElement: RouteError,
    handle: { meta: { title: 'basePages.login', public: true } },
  },
  {
    path: '/oauth/consent',
    element: (
      <Suspense fallback={LoadingFallback}>
        <OAuthConsent />
      </Suspense>
    ),
    errorElement: RouteError,
    handle: { meta: { title: 'oauthConsent.title', public: true } },
  },
  {
    path: '/onboarding',
    element: (
      <TabOrganizationGate rejectedFallback={OrganizationForbidden}>
        <PermissionGate minRole={ROLE.VIEWER} loadingFallback={LoadingFallback}>
          <Suspense fallback={LoadingFallback}>
            <Onboarding />
          </Suspense>
        </PermissionGate>
      </TabOrganizationGate>
    ),
    errorElement: RouteError,
    handle: { meta: { title: 'basePages.onboarding' } },
  },
  {
    path: '/in-maintenance',
    element: (
      <TabOrganizationGate rejectedFallback={OrganizationForbidden}>
        <PermissionGate minRole={ROLE.VIEWER} loadingFallback={LoadingFallback}>
          <Suspense fallback={LoadingFallback}>
            <InMaintenancePage />
          </Suspense>
        </PermissionGate>
      </TabOrganizationGate>
    ),
    errorElement: RouteError,
  },
  {
    path: '/403',
    element: (
      <Suspense fallback={LoadingFallback}>
        <Forbidden />
      </Suspense>
    ),
    errorElement: RouteError,
    handle: { meta: { title: 'basePages.forbidden', public: true } },
  },
  {
    path: '*',
    element: (
      <Suspense fallback={LoadingFallback}>
        <NotFound />
      </Suspense>
    ),
    errorElement: RouteError,
    handle: { meta: { title: 'basePages.notFound', public: true } },
  },
])

keepOrganizationInUrl(router)
